import { type ScoredHint, type Hint, Rarity } from "./types";
import { findAllSolutions } from "./solver";

const RARITY_ORDER: readonly Rarity[] = [
  Rarity.COMMON,
  Rarity.UNCOMMON,
  Rarity.RARE,
  Rarity.EPIC,
  Rarity.LEGENDARY,
];

function getTargetMix(
  baseHintTarget: number,
): Record<Rarity, number> {
  if (baseHintTarget >= 5) {
    return {
      [Rarity.COMMON]: 1,
      [Rarity.UNCOMMON]: 1,
      [Rarity.RARE]: 1,
      [Rarity.EPIC]: 1,
      [Rarity.LEGENDARY]: 1,
    };
  }
  if (baseHintTarget === 4) {
    return {
      [Rarity.COMMON]: 1,
      [Rarity.UNCOMMON]: 1,
      [Rarity.RARE]: 1,
      [Rarity.EPIC]: 1,
      [Rarity.LEGENDARY]: 0,
    };
  }
  return {
    [Rarity.COMMON]: 1,
    [Rarity.UNCOMMON]: 1,
    [Rarity.RARE]: 1,
    [Rarity.EPIC]: 0,
    [Rarity.LEGENDARY]: 0,
  };
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function assemblePuzzle(
  hintPool: readonly ScoredHint[],
  codeLength: number,
  baseHintTarget: number = 5,
  maxAttempts: number = 150,
): { hints: ScoredHint[]; isUnique: boolean } {
  const cleanHints = hintPool.filter(
    (h) => h.power_up === null || h.power_up === undefined,
  );

  const byRarity: Record<Rarity, ScoredHint[]> = {
    [Rarity.COMMON]: [],
    [Rarity.UNCOMMON]: [],
    [Rarity.RARE]: [],
    [Rarity.EPIC]: [],
    [Rarity.LEGENDARY]: [],
  };

  for (const h of cleanHints) {
    if (h.rarity !== null) {
      byRarity[h.rarity].push(h);
    }
  }

  const targetMix = getTargetMix(baseHintTarget);

  let bestHints: ScoredHint[] | null = null;
  let bestSolutionCount = Infinity;
  const isUnique = false;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    for (const rarity of RARITY_ORDER) {
      byRarity[rarity] = shuffleArray(byRarity[rarity]);
    }

    const candidate: ScoredHint[] = [];
    for (const rarity of RARITY_ORDER) {
      if (targetMix[rarity] > 0 && byRarity[rarity].length > 0) {
        candidate.push(byRarity[rarity][0]);
      }
    }

    if (candidate.length < 2) {
      continue;
    }

    const solverHints: Hint[] = candidate.map((h) => h.hint);
    const solutions = findAllSolutions(codeLength, solverHints);

    if (solutions.length === 1) {
      return { hints: candidate, isUnique: true };
    }

    if (bestHints === null || solutions.length < bestSolutionCount) {
      bestHints = candidate;
      bestSolutionCount = solutions.length;
    }
  }

  if (bestHints !== null) {
    return { hints: bestHints, isUnique };
  }

  const fallback = cleanHints.slice(0, baseHintTarget);
  return { hints: [...fallback], isUnique: false };
}
